import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Save, Plus, Trash2, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const FIELDS = [
  ["wave_size", "Pros per round", "How many best-matched pros get each round of the request", 1, 50, 1],
  ["wave_interval_hours", "Hours between rounds", "Wait this long before sending the next round automatically", 0.25, 720, 0.25],
  ["max_waves", "Maximum rounds", "Total rounds (including the first) a request can be sent out", 1, 10, 1],
  ["target_estimates", "Estimate target", "Stop sending new rounds once this many estimates arrive", 1, 50, 1],
  ["opportunity_ttl_hours", "Hours a pro has to respond", "After this, an unanswered match expires", 1, 720, 1],
];

const NumField = ({ f, value, placeholder, onChange, disabled, tid }) => (
  <input type="number" min={f[3]} max={f[4]} step={f[5]} value={value ?? ""} placeholder={placeholder} disabled={disabled} onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))} className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-[#165a71] disabled:bg-slate-50" data-testid={tid} />
);

export default function AdminMatchingPage() {
  const { user } = useAuth();
  const canEdit = (user?.roles || []).some((r) => ["administrator", "super_administrator"].includes(r));
  const [d, setD] = useState(null);
  const [g, setG] = useState({});
  const [cats, setCats] = useState([]);
  const [newCat, setNewCat] = useState("");
  const [drafts, setDrafts] = useState({});
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/service-requests/matching-settings").then((r) => {
    setD(r.data); setG(r.data.global);
    setDrafts(Object.fromEntries(r.data.overrides.map((o) => [o.category_id, o.settings])));
  });
  useEffect(() => { load(); api.get("/categories").then((r) => setCats((r.data || []).filter((c) => !c.parent_id && !/^test/i.test(c.slug || "")))); }, []);
  if (!d) return <div className="flex items-center gap-2 text-[#252A34]/60"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>;

  const rangeError = (vals, partial) => {
    for (const f of FIELDS) {
      const v = vals[f[0]];
      if (v == null) { if (partial) continue; return `${f[1]} is required`; }
      if (v < f[3] || v > f[4]) return `${f[1]} must be between ${f[3]} and ${f[4]}`;
    }
    return null;
  };
  const saveGlobal = async () => {
    const e = rangeError(g, false);
    if (e) return toast.error(e);
    setSaving(true);
    try { await api.put("/admin/service-requests/matching-settings", g); toast.success("Matching defaults saved"); load(); }
    catch (err) { toast.error(err?.response?.data?.error || "Could not save"); } finally { setSaving(false); }
  };
  const saveOverride = async (cid, settings) => {
    const e = settings && rangeError(settings, true);
    if (e) return toast.error(e);
    try { await api.put("/admin/service-requests/matching-settings/overrides", { category_id: cid, settings }); toast.success(settings ? "Override saved" : "Override removed"); setNewCat(""); load(); }
    catch (err) { toast.error(err?.response?.data?.error || "Could not save override"); }
  };
  const used = new Set(d.overrides.map((o) => o.category_id));
  const catName = (id) => cats.find((c) => c.id === id)?.name || d.overrides.find((o) => o.category_id === id)?.category?.name || id;

  return (
    <div className="max-w-5xl" data-testid="admin-matching">
      <Link to="/admin/questionnaires" className="text-sm text-[#165a71]">← Questionnaires</Link>
      <h1 className="mt-2 font-display text-2xl md:text-3xl font-semibold text-[#071b22]">Matching settings</h1>
      <p className="text-sm text-[#252A34]/65 mt-1">Control how service requests are sent to pros. Changes apply to rounds sent from now on.</p>
      {!canEdit && <div className="mt-3 rounded-xl bg-slate-100 px-4 py-2 text-xs" data-testid="matching-readonly-note">View only — administrators can change these settings.</div>}

      <div className="mt-6 rounded-3xl border border-slate-200 bg-white p-6">
        <div className="font-display text-lg font-semibold text-[#071b22]">Global defaults</div>
        <div className="mt-4 grid gap-5 sm:grid-cols-2">
          {FIELDS.map((f) => (
            <label key={f[0]} className="block">
              <div className="text-sm font-medium text-[#071b22]">{f[1]}</div>
              <div className="text-xs text-[#252A34]/60 mb-1.5">{f[2]}</div>
              <NumField f={f} value={g[f[0]]} disabled={!canEdit} onChange={(v) => setG({ ...g, [f[0]]: v })} tid={`matching-global-${f[0]}`} />
            </label>
          ))}
        </div>
        {canEdit && <button onClick={saveGlobal} disabled={saving} className="mt-6 rounded-full bg-[#165a71] text-white px-5 py-2.5 text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-60" data-testid="matching-global-save"><Save className="h-4 w-4" /> Save defaults</button>}
      </div>

      <div className="mt-6 rounded-3xl border border-slate-200 bg-white p-6" data-testid="matching-overrides">
        <div className="font-display text-lg font-semibold text-[#071b22]">Category overrides</div>
        <p className="text-xs text-[#252A34]/60 mt-1">Leave a field blank to use the global default for that category.</p>
        <div className="mt-4 space-y-4">
          {Object.keys(drafts).map((cid) => (
            <div key={cid} className="rounded-2xl border border-slate-100 bg-slate-50/60 p-4" data-testid={`override-${cid}`}>
              <div className="flex items-center justify-between"><div className="font-medium text-[#071b22]">{catName(cid)}</div>
                {canEdit && <button onClick={() => (used.has(cid) ? saveOverride(cid, null) : setDrafts((x) => { const y = { ...x }; delete y[cid]; return y; }))} className="text-rose-700 inline-flex items-center gap-1 text-xs" data-testid={`override-remove-${cid}`}><Trash2 className="h-3.5 w-3.5" /> Remove</button>}
              </div>
              <div className="mt-3 grid gap-3 grid-cols-2 md:grid-cols-5">
                {FIELDS.map((f) => (
                  <label key={f[0]} className="block"><div className="text-[11px] text-[#252A34]/70 mb-1">{f[1]}</div>
                    <NumField f={f} value={drafts[cid][f[0]]} placeholder={String(g[f[0]])} disabled={!canEdit} onChange={(v) => setDrafts((x) => ({ ...x, [cid]: { ...x[cid], [f[0]]: v } }))} tid={`override-${cid}-${f[0]}`} />
                  </label>
                ))}
              </div>
              {canEdit && <button onClick={() => saveOverride(cid, Object.fromEntries(Object.entries(drafts[cid]).filter(([, v]) => v != null)))} className="mt-3 rounded-full bg-[#071b22] text-white px-4 py-2 text-xs font-semibold" data-testid={`override-save-${cid}`}>Save override</button>}
            </div>
          ))}
          {!Object.keys(drafts).length && <div className="text-sm text-[#252A34]/60" data-testid="overrides-empty">No overrides — every category uses the global defaults.</div>}
        </div>
        {canEdit && (
          <div className="mt-5 flex gap-2">
            <select value={newCat} onChange={(e) => setNewCat(e.target.value)} className="flex-1 rounded-xl border border-slate-200 px-3 py-2 text-sm" data-testid="override-new-category">
              <option value="">Add an override for…</option>
              {cats.filter((c) => !(c.id in drafts)).map((c) => <option key={c.id} value={c.id}>{`${c.name} (${c.kind})`}</option>)}
            </select>
            <button disabled={!newCat} onClick={() => { setDrafts((x) => ({ ...x, [newCat]: {} })); setNewCat(""); }} className="rounded-full border border-[#165a71]/40 text-[#165a71] px-4 py-2 text-sm inline-flex items-center gap-1 disabled:opacity-50" data-testid="override-add"><Plus className="h-4 w-4" /> Add</button>
          </div>
        )}
      </div>
    </div>
  );
}
